// code-guardian-ignore-duplicates: parallel master-page shell (tab kept in ?tab=, selectTab, the StoreMasterPage tab strip + action slot) copied on purpose so every tabbed page matches
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Users, ClipboardList, Map, ListChecks } from 'lucide-react';
import BuddyPoolPage from './BuddyPoolPage';
import OnboardingSignoffFormPage from './OnboardingSignoffFormPage';
import OnboardingChecklistFormPage from './OnboardingChecklistFormPage';
import OnboardingJourneyPage from './OnboardingJourneyPage';

/**
 * Grow → Onboarding Setup. First "Shadow Training", then "Onboarding"; that name is
 * the Onboarding dashboard's, so this page says what it is for. Its menu code and
 * address still say shadow_training / shadow-training.
 *
 * The single home for everything that CONFIGURES a new joiner's shadow training
 * week: who may host them (Buddy Pool) and what their host is asked each day
 * (Sign-off Form). These shipped as two separate Master Data pages; merging them
 * puts the setup for one workflow in one place, and moves it next to the other
 * Grow surfaces rather than sitting among the reference-data lists.
 *
 * The two tabs are the existing pages in `embedded` mode — same convention as
 * TargetsPage inside EmployeeScorePage — so neither has to be rewritten or
 * forked. Each keeps its own data fetching, access handling and modals; this
 * shell only supplies the heading and the tab strip.
 *
 * Note this page is CONFIGURATION only. The running worklist — who joined, who
 * they were paired with, and ending a placement — stays on Dashboard →
 * Onboarding, because that is operational day-to-day work rather than setup.
 */

type Tab = 'buddies' | 'form' | 'checklist' | 'journey';

const TABS: { key: Tab; label: string; icon: typeof Users; blurb: string }[] = [
  {
    key: 'journey',
    label: 'Journey',
    icon: Map,
    blurb:
      'The new joiner’s first fortnight — the activities they attend each day (induction, training, and the shadow week), with times, hosts and join links. Day 1 is their joining day.',
  },
  {
    key: 'buddies',
    label: 'Buddy Pool',
    icon: Users,
    blurb:
      "Experienced employees who can host a new joiner, tagged with the area they cover. Dashboard → Onboarding suggests these first, matching the joiner's city and then their state.",
  },
  {
    key: 'form',
    label: 'Sign-off Form',
    icon: ClipboardList,
    blurb:
      'The questions the buddy answers during shadow training. Each form covers a stretch of the period — Day 1 is the joiner’s first shadow-training day, whatever the date.',
  },
  {
    key: 'checklist',
    label: 'Checklist',
    icon: ListChecks,
    blurb:
      'The questions the NEW JOINER answers themselves, day by day, across their onboarding plan — based on the activities they attend. Day 1 is their joining day, whatever the date.',
  },
];

export default function ShadowTrainingPage() {
  // The tab lives in the URL so a link can point straight at either half, and so
  // the browser back button steps between them rather than leaving the page.
  const [searchParams, setSearchParams] = useSearchParams();
  const fromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<Tab>(
    fromUrl === 'form' ? 'form'
      : fromUrl === 'buddies' ? 'buddies'
        : fromUrl === 'checklist' ? 'checklist'
          : 'journey',
  );

  // The tab strip and the active tab's primary action share one row. Rather than
  // hoisting each tab's action up here — they differ per tab and per view — the
  // shell exposes a DOM node and each tab portals its own button into it, so
  // ownership of the action stays with the page that handles it. A callback ref
  // (not useRef) because the children must re-render once the node exists.
  const [actionSlot, setActionSlot] = useState<HTMLDivElement | null>(null);

  const selectTab = (tab: Tab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  const current = TABS.find((t) => t.key === activeTab) ?? TABS[0];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Onboarding Setup</h1>
        <p className="mt-1 text-sm text-gray-500">{current.blurb}</p>
      </div>

      {/* Tabs left, the active tab's primary action right — one row, no empty
          band between them. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = t.key === activeTab;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => selectTab(t.key)}
                className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${
                  isActive ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>
        <div ref={setActionSlot} className="flex items-center gap-2" />
      </div>

      {/* Both tabs stay mounted-on-demand rather than always: each fetches on
          mount, and rendering the hidden one would double the requests on every
          page load for a tab the user may never open. */}
      {activeTab === 'buddies'
        ? <BuddyPoolPage embedded actionSlot={actionSlot} />
        : activeTab === 'form'
          ? <OnboardingSignoffFormPage embedded actionSlot={actionSlot} />
          : activeTab === 'checklist'
            ? <OnboardingChecklistFormPage embedded actionSlot={actionSlot} />
            : <OnboardingJourneyPage embedded actionSlot={actionSlot} />}
    </div>
  );
}
